import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Calculator,
  ChevronRight,
  Home,
  Search,
  Sparkles,
  ArrowRight
} from 'lucide-react';

// Calculator data organized by category
const calculatorCategories = [
  {
    title: 'Arithmetic & Number Theory',
    icon: '🔢',
    color: 'from-blue-500 to-blue-600',
    calculators: [
      { name: 'Fraction Simplifier & Operator', path: '/calculators/fraction', icon: '½' },
      { name: 'Prime Number & Factorization Tool', path: '/calculators/prime', icon: '📊' },
      { name: 'LCM / GCD Finder', path: '/calculators/lcm-gcd', icon: '🎯' },
      { name: 'Exponent & Radical Simplifier', path: '/calculators/exponent', icon: '²√' },
      { name: 'Scientific Notation Converter', path: '/calculators/scientific-notation', icon: '🔬' }
    ]
  },
  {
    title: 'Algebra',
    icon: '𝑥',
    color: 'from-purple-500 to-purple-600',
    calculators: [
      { name: 'Linear Equation Solver (2 variables) + Graph', path: '/calculators/linear-equation', icon: '📈' },
      { name: 'Quadratic Formula Solver + Parabola Graph', path: '/calculators/quadratic-solver', icon: '∩' },
      { name: 'System of Equations Solver (2 & 3 variables)', path: '/calculators/system-equations', icon: '🔀' },
      { name: 'Polynomial Long Division & Factorization', path: '/calculators/polynomial', icon: '📐' },
      { name: 'Sequences & Series Calculator', path: '/calculators/sequences', icon: '∑' },
      { name: 'Matrix Calculator (Determinant, Inverse, Operations)', path: '/calculators/matrix', icon: '⬜' },
      { name: 'Logarithm & Exponential Equation Solver', path: '/calculators/logarithm', icon: '㏑' }
    ]
  },
  {
    title: 'Geometry & Trigonometry',
    icon: '△',
    color: 'from-green-500 to-green-600',
    calculators: [
      { name: 'Triangle Solver (SSS, SAS, ASA, etc.)', path: '/calculators/triangle-solver', icon: '△' },
      { name: 'Circle Properties Calculator', path: '/calculators/circle', icon: '◯' },
      { name: 'Polygon Properties', path: '/calculators/polygon', icon: '⬡' },
      { name: '3D Shape Calculator', path: '/calculators/shape-3d', icon: '🔷' },
      { name: 'Trigonometric Identity Verifier & Solver', path: '/calculators/trig-identity', icon: '⟷' },
      { name: 'Trigonometry Calculator', path: '/calculators/trigonometry', icon: '📐' }
    ]
  },
  {
    title: 'Calculus',
    icon: '∫',
    color: 'from-orange-500 to-orange-600',
    calculators: [
      { name: 'Derivative Calculator', path: '/calculators/derivative', icon: 'd/dx' },
      { name: 'Integral Calculator', path: '/calculators/integral', icon: '∫' },
      { name: 'Limit Calculator', path: '/calculators/limit', icon: 'lim' },
      { name: 'Function Grapher & Analyzer', path: '/calculators/function-grapher', icon: '📊' }
    ]
  },
  {
    title: 'Probability & Statistics',
    icon: '📈',
    color: 'from-red-500 to-red-600',
    calculators: [
      { name: 'Descriptive Statistics Calculator', path: '/calculators/statistics', icon: '📊' },
      { name: 'Probability Distributions (Binomial, Normal)', path: '/calculators/probability', icon: '🔔' },
      { name: 'Permutations (nPr) & Combinations (nCr)', path: '/calculators/permutations', icon: '🎲' },
      { name: 'Regression Line Calculator', path: '/calculators/regression', icon: '📈' }
    ]
  }
];

// Calculator Card Component
const CalculatorCard = ({ name, path, icon, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.3, delay }}
  >
    <Link to={path}>
      <Card className="calculator-card group cursor-pointer h-full p-4 flex items-center gap-4 hover:shadow-lg transition-all duration-300 hover:scale-105">
        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center text-lg font-bold group-hover:from-primary/30 group-hover:to-accent/30 transition-colors flex-shrink-0">
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-foreground group-hover:text-primary transition-colors truncate">
            {name}
          </h4>
        </div>
        <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0 group-hover:translate-x-1" />
      </Card>
    </Link>
  </motion.div>
);

// Category Section Component
const CategorySection = ({ category, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay: index * 0.1 }}
  >
    <div className="mb-12">
      {/* Category Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${category.color} flex items-center justify-center text-xl`}>
          {category.icon}
        </div>
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground">{category.title}</h2>
          <p className="text-sm text-muted-foreground">{category.calculators.length} tools available</p>
        </div>
      </div>

      {/* Calculators Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {category.calculators.map((calc, i) => (
          <CalculatorCard
            key={i}
            name={calc.name}
            path={calc.path}
            icon={calc.icon}
            delay={i * 0.05}
          />
        ))}
      </div>
    </div>
  </motion.div>
);

export default function ExploreCalculators() {
  const [searchTerm, setSearchTerm] = useState('');

  // Filter calculators based on search
  const filteredCategories = calculatorCategories.map(cat => ({
    ...cat,
    calculators: cat.calculators.filter(calc =>
      calc.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(cat => cat.calculators.length > 0);

  const totalCalculators = calculatorCategories.reduce((sum, cat) => sum + cat.calculators.length, 0);
  const filteredCount = filteredCategories.reduce((sum, cat) => sum + cat.calculators.length, 0);

  return (
    <>
      <Helmet>
        <title>Explore Calculators - MathCalcPro</title>
        <meta name="description" content="Explore all interactive math calculators including fraction simplifier, polynomial solver, derivative calculator, and more. Organized by mathematical discipline." />
        <meta name="keywords" content="math calculators, fraction calculator, polynomial solver, derivative calculator, integral calculator" />
        <link rel="canonical" href="https://mathcalcpro.com/explore" />
      </Helmet>

      {/* Header */}
      <section className="relative overflow-hidden py-12 md:py-20 bg-gradient-to-b from-muted/50 to-background">
        <div className="absolute inset-0">
          <div className="absolute top-10 left-5 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-5 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4 relative">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-8"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              <Sparkles className="w-4 h-4" />
              <span>Complete Calculator Suite</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-4">
              Explore All{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">
                Calculators
              </span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Discover our complete collection of {totalCalculators} interactive math tools, organized by category. Find exactly what you need to solve your math problems.
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto relative">
              <Search className="absolute left-4 top-3.5 w-5 h-5 text-muted-foreground" />
              <Input
                placeholder="Search calculators (e.g., fraction, derivative, trigonometry)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 h-12 rounded-xl border-2 border-muted-foreground/20 hover:border-primary/40 focus:border-primary transition-colors"
              />
            </div>
            {searchTerm && (
              <p className="text-sm text-muted-foreground mt-4">
                Found {filteredCount} calculator{filteredCount !== 1 ? 's' : ''} matching "{searchTerm}"
              </p>
            )}
          </motion.div>

          {/* Quick Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="grid grid-cols-3 gap-4 max-w-2xl mx-auto"
          >
            {[
              { value: totalCalculators, label: 'Total Tools' },
              { value: calculatorCategories.length, label: 'Categories' },
              { value: '100%', label: 'Free to Use' }
            ].map((stat, i) => (
              <Card key={i} className="p-4 text-center">
                <div className="text-2xl font-bold text-primary">{stat.value}</div>
                <div className="text-xs text-muted-foreground">{stat.label}</div>
              </Card>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Calculators Grid */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          {searchTerm && filteredCount === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-12"
            >
              <Calculator className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
              <h3 className="text-xl font-semibold text-foreground mb-2">No calculators found</h3>
              <p className="text-muted-foreground mb-6">
                Try adjusting your search terms or browse all categories below
              </p>
              <Button
                onClick={() => setSearchTerm('')}
                variant="outline"
                className="rounded-lg"
              >
                Clear Search
              </Button>
            </motion.div>
          ) : (
            <div className="space-y-16">
              {filteredCategories.map((category, index) => (
                <CategorySection
                  key={index}
                  category={category}
                  index={index}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      {!searchTerm && (
        <section className="py-16 md:py-24 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-3xl mx-auto text-center"
            >
              <div className="neumorph rounded-3xl p-8 md:p-12">
                <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
                  Ready to Get Started?
                </h2>
                <p className="text-muted-foreground mb-8 max-w-lg mx-auto">
                  Choose any calculator above to begin solving problems with step-by-step solutions and beautiful visualizations.
                </p>
                <Link to="/calculators/trigonometry">
                  <Button size="lg" className="rounded-xl gap-2 px-8 h-12 shadow-glow">
                    <span>Try Calculator</span>
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      )}
    </>
  );
}
